import React, {FunctionComponent} from "react";
import {Box, SxProps, Theme, Typography} from "@mui/material";
import {AxisHoverParam} from "@atiautomacao/ati-ui-library";

interface ChartBottomHintProps {
    /** Valores recebidos via `onAxisHover` do AChartX; `null`/vazio oculta o hint. */
    params: AxisHoverParam[] | null;
    /** Unidade exibida após o valor de cada série (ex.: kW, PU, W/m²). */
    getUnit?: (param: AxisHoverParam) => string | undefined;
    /** Texto do cabeçalho (horário). Por padrão usa o rótulo do eixo X. */
    formatHeader?: (param: AxisHoverParam) => string;
    /** Formatação customizada do valor de cada série; quando informada, substitui o valor bruto + `getUnit`. */
    formatValue?: (param: AxisHoverParam) => string;
    /** Largura mínima de cada coluna (px); reduzir para séries de nome curto (ex.: trackers). */
    itemMinWidth?: number;
    /** Espaço entre colunas (px). */
    columnGap?: number;
    sx?: SxProps<Theme>;
}

const EMPTY_VALUE = "-";
const ITEM_MIN_WIDTH = 200;
const COLUMN_GAP = 12;
// Abaixo desta largura do hint, cada item passa a ter o nome em cima e o valor embaixo.
const STACKED_MAX_WIDTH = 480;

// Em eixo 'time' o value vem como [x, y]; em eixo 'category' vem só o y.
const extractValue = (value: unknown): unknown => Array.isArray(value) ? value[1] : value;

const formatValue = (param: AxisHoverParam, unit?: string): string => {
    const value = extractValue(param.value);
    if (value === null || value === undefined || value === "" || value === EMPTY_VALUE) {
        return EMPTY_VALUE;
    }
    return unit ? `${value} ${unit}` : String(value);
};

const defaultFormatHeader = (param: AxisHoverParam): string => String(param.axisValueLabel ?? param.axisValue ?? "");

/**
 * Hint de gráfico renderizado abaixo da área de plotagem (padrão da tela de Inversor detalhada),
 * para não sobrepor as curvas. Mantém a ordem das séries recebida do gráfico.
 */
const ChartBottomHint: FunctionComponent<ChartBottomHintProps> = ({
    params,
    getUnit,
    formatHeader = defaultFormatHeader,
    formatValue: customFormatValue,
    itemMinWidth = ITEM_MIN_WIDTH,
    columnGap = COLUMN_GAP,
    sx
}) => {
    if (!params || params.length === 0) {
        return null;
    }

    return (
        <Box
            data-testid="chart-bottom-hint"
            aria-label="Valores do ponto selecionado no gráfico"
            sx={{
                containerType: 'inline-size',
                display: 'grid',
                // Sempre ao menos 2 colunas: a largura mínima da coluna é o menor valor entre
                // itemMinWidth e metade do espaço disponível; em telas largas cabem mais colunas.
                gridTemplateColumns: `repeat(auto-fill, minmax(min(${itemMinWidth}px, calc((100% - ${columnGap}px) / 2)), 1fr))`,
                columnGap: `${columnGap}px`,
                rowGap: '6px',
                alignItems: 'start',
                mt: 1,
                p: 1.5,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px',
                // Hint estreito (cards em grid): nome em cima e valor embaixo, para manter 2 colunas
                // legíveis mesmo com muitos inversores.
                [`@container (max-width: ${STACKED_MAX_WIDTH}px)`]: {
                    '& .chart-bottom-hint-item': {flexDirection: 'column', alignItems: 'flex-start'},
                    '& .chart-bottom-hint-label': {maxWidth: '100%', fontSize: '13px'},
                    '& .chart-bottom-hint-value': {ml: '12px', fontSize: '13px'},
                },
                ...sx,
            }}
        >
            <Typography
                component="span"
                sx={{gridColumn: '1 / -1', fontSize: '14px', fontWeight: 'bold', color: 'text.primary', pb: 0.5, borderBottom: 1, borderColor: 'divider'}}
            >
                {formatHeader(params[0])}
            </Typography>
            {params.map((param) => (
                <Box
                    key={`${param.seriesIndex}-${param.seriesName}`}
                    className="chart-bottom-hint-item"
                    sx={{display: 'flex', alignItems: 'center', minWidth: 0, lineHeight: 1.3}}
                >
                    <Box className="chart-bottom-hint-label" sx={{display: 'flex', alignItems: 'center', minWidth: 0, fontSize: '14px'}}>
                        <Box
                            aria-hidden
                            sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: param.color, mr: 0.5, flexShrink: 0}}
                        />
                        {/* Nome com reticências só no limite (completo no title); o valor nunca é cortado. */}
                        <Typography
                            component="span"
                            title={param.seriesName}
                            sx={{fontSize: 'inherit', lineHeight: 'inherit', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0}}
                        >
                            {param.seriesName}:
                        </Typography>
                    </Box>
                    {" "}
                    <Box
                        component="b"
                        className="chart-bottom-hint-value"
                        sx={{fontSize: '14px', lineHeight: 'inherit', color: 'text.primary', whiteSpace: 'nowrap', flexShrink: 0, ml: 0.5}}
                    >
                        {customFormatValue ? customFormatValue(param) : formatValue(param, getUnit?.(param))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default ChartBottomHint;
